import { Popover, styled, Chip } from "@mui/material";
import clsx from "clsx";
import { Button } from "components";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import "react-day-picker/style.css";
import { useTranslation } from "react-i18next";

const TopRowContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(1, 2),
}));
const YearChipsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(0.25),
}));

const YearChip = styled(Chip)(({ theme }) => ({
  fontSize: "0.875rem",
  fontWeight: 500,
  height: "auto",
  padding: theme.spacing(0.5, 1),
  border: "none",
  backgroundColor: "transparent",
  // "&.MuiChip-colorPrimary": {
  //   backgroundColor: theme.palette.primary.main,
  //   color: theme.palette.primary.contrastText,
  // },
  "& .MuiChip-label": {
    padding: theme.spacing(0.5, 1),
  },
}));

const ReactDayPickerStyled = styled(DayPicker)(({ theme }) => ({
  "& .months": {
    "& .month": {
      // padding: theme.spacing(1.5, 1.5, 3),
      // "& .button_next": {
      //   top: theme.spacing(1.5),
      //   right: theme.spacing(1.5),
      // },
      // "& .button_previous": {
      //   top: theme.spacing(1.5),
      //   left: theme.spacing(1.5),
      // },
    },
  },
  "& .button_next, .button_previous": {
    "& svg": {
      fill: theme.palette.text.primary,
    },
  },

  "& .caption_label": {
    fontSize: "1rem",
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  "& .day": {
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
  },
  "& .day_button": {
    fontSize: "0.875rem",
    width: theme.spacing(4.5),
    height: theme.spacing(4.5),
  },
  // apply selected styles except for range middle days
  "& .selected:not(.range_middle)": {
    "& .day_button": {
      border: "none",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 400,
    },
  },
  "& .today": {
    "& .day_button": {
      border: `1px solid ${theme.palette.grey[600]}`,
      color: theme.palette.primary.main,
    },
  },
}));

const DefaultClassnames = getDefaultClassNames();

const CommonDayPicker = ({
  value,
  innerValue,
  isOpen,
  anchorEl,
  variant,
  showYears,
  selectYears,
  minDate,
  maxDate,
  minSpan,
  maxSpan,
  disableDate,
  defaultMonth,
  onChange,
  onClose,
  onMonthChange,
}) => {
  const { t } = useTranslation();
  const [currentMonth, setCurrentMonth] = useState(moment());

  const startMonth = useMemo(() => {
    return minDate ? moment(minDate) : null;
  }, [minDate]);

  const endMonth = useMemo(() => {
    return maxDate ? moment(maxDate) : null;
  }, [maxDate]);

  const handleYearClick = (year) => {
    let yearStart = moment(`${year}-01-01`).toDate();
    let yearEnd = moment(`${year}-12-31`).toDate();

    // Respect minDate - if minDate is within the selected year, use it as start
    if (minDate && moment(minDate).year() === parseInt(year)) {
      yearStart = moment(minDate).toDate();
    }

    // Respect maxDate - if maxDate is within the selected year, use it as end
    if (maxDate && moment(maxDate).year() === parseInt(year)) {
      yearEnd = moment(maxDate).toDate();
    }

    // Respect minSpan - if range is smaller than minSpan, adjust end date
    if (minSpan) {
      const daysDiff = moment(yearEnd).diff(moment(yearStart), "days") + 1;
      if (daysDiff < minSpan) {
        yearEnd = moment(yearStart)
          .add(minSpan - 1, "days")
          .toDate();
        // Make sure we don't exceed maxDate
        if (maxDate && moment(yearEnd).isAfter(moment(maxDate))) {
          yearEnd = moment(maxDate).toDate();
        }
      }
    }

    // Respect maxSpan - if range is larger than maxSpan, adjust end date
    if (maxSpan) {
      const daysDiff = moment(yearEnd).diff(moment(yearStart), "days") + 1;
      if (daysDiff > maxSpan) {
        yearEnd = moment(yearStart)
          .add(maxSpan - 1, "days")
          .toDate();
      }
    }

    onChange({ from: yearStart, to: yearEnd });
  };

  const handleMonthClick = useCallback(
    (monthDate) => {
      let monthStart = moment(monthDate).startOf("month").toDate();
      let monthEnd = moment(monthDate).endOf("month").toDate();

      // Respect minDate - if minDate is within the selected month, use it as start
      if (minDate && moment(minDate).isSame(monthDate, "month")) {
        monthStart = moment.max(moment(monthStart), moment(minDate)).toDate();
      }

      // Respect maxDate - if maxDate is within the selected month, use it as end
      if (maxDate && moment(maxDate).isSame(monthDate, "month")) {
        monthEnd = moment.min(moment(monthEnd), moment(maxDate)).toDate();
      }

      // Respect minSpan - if range is smaller than minSpan, adjust end date
      if (minSpan) {
        const daysDiff = moment(monthEnd).diff(moment(monthStart), "days") + 1;
        if (daysDiff < minSpan) {
          monthEnd = moment(monthStart)
            .add(minSpan - 1, "days")
            .toDate();
          // Make sure we don't exceed maxDate
          if (maxDate && moment(monthEnd).isAfter(moment(maxDate))) {
            monthEnd = moment(maxDate).toDate();
          }
        }
      }

      // Respect maxSpan - if range is larger than maxSpan, adjust end date
      if (maxSpan) {
        const daysDiff = moment(monthEnd).diff(moment(monthStart), "days") + 1;
        if (daysDiff > maxSpan) {
          monthEnd = moment(monthStart)
            .add(maxSpan - 1, "days")
            .toDate();
        }
      }

      onChange({ from: monthStart, to: monthEnd });
    },
    [minDate, maxDate, minSpan, maxSpan, onChange]
  );

  // Add click listeners to month labels
  useEffect(() => {
    if (!isOpen) return;

    const handleCaptionClick = (event) => {
      const labelText = event.target.textContent;
      // Parse the label text (e.g., "March 2025")
      const monthDate = moment(labelText, "MMMM YYYY");
      if (monthDate.isValid()) {
        handleMonthClick(monthDate.toDate());
      }
    };

    const attachListeners = () => {
      const captionLabels = document.querySelectorAll(".caption_label");

      if (captionLabels.length === 0) {
        // If elements aren't ready yet, try again after a short delay
        setTimeout(attachListeners, 50);
        return;
      }

      captionLabels.forEach((label) => {
        const labelText = label.textContent;
        const monthDate = moment(labelText, "MMMM YYYY");

        if (!monthDate.isValid()) return;

        // Check if this month is out of range - if so, skip adding listener
        if (
          checkIfMonthIsOutOfRange({
            monthDate: monthDate.toDate(),
            minDate,
            maxDate,
          })
        ) {
          return;
        }

        // This month is at least partially within range, add listener
        label.addEventListener("click", handleCaptionClick);
      });
    };

    // Start trying to attach listeners
    const timeoutId = setTimeout(attachListeners, 0);

    return () => {
      clearTimeout(timeoutId);
      const captionLabels = document.querySelectorAll(".caption_label");
      captionLabels.forEach((label) => {
        label.removeEventListener("click", handleCaptionClick);
      });
    };
  }, [isOpen, handleMonthClick, minDate, maxDate, currentMonth]);

  const isYearSelected = useCallback(
    (year) => {
      return checkIfYearIsSelected({ year, minDate, maxDate, value });
    },
    [minDate, maxDate, value]
  );

  return (
    <>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <TopRowContainer>
          {selectYears && selectYears.length > 0 ? (
            <YearChipsContainer>
              {selectYears.map((year) => (
                <YearChip
                  key={year}
                  label={year}
                  onClick={() => handleYearClick(year)}
                  color={isYearSelected(year) ? "primary" : "default"}
                  variant="outlined"
                />
              ))}
            </YearChipsContainer>
          ) : (
            <span />
          )}
          <Button variant="text" onClick={() => setCurrentMonth(moment())}>
            {t("Global.Today")}
          </Button>
        </TopRowContainer>
        <ReactDayPickerStyled
          animate
          mode={variant}
          month={currentMonth}
          startMonth={startMonth}
          endMonth={endMonth}
          numberOfMonths={variant === "range" ? 2 : 1}
          selected={value ?? innerValue}
          defaultMonth={defaultMonth}
          navLayout="around"
          captionLayout={showYears ? "dropdown-years" : "label"}
          min={minSpan}
          max={maxSpan}
          disabled={[{ before: minDate, after: maxDate }, disableDate]}
          formatters={{
            formatCaption: (month) => moment(month).format("MMMM YYYY"),
            formatWeekdayName: (day) => moment(day).format("dd"),
          }}
          classNames={{
            button_next: clsx(DefaultClassnames.button_next, "button_next"),
            button_previous: clsx(
              DefaultClassnames.button_previous,
              "button_previous"
            ),
            caption_label: clsx(
              DefaultClassnames.caption_label,
              "caption_label"
            ),
            month: clsx(DefaultClassnames.month, "month"),
            months: clsx(DefaultClassnames.months, "months"),
            day: clsx(DefaultClassnames.day, "day"),
            day_button: clsx(DefaultClassnames.day_button, "day_button"),
            selected: clsx(DefaultClassnames.selected, "selected"),
            range_middle: clsx(DefaultClassnames.range_middle, "range_middle"),
            today: clsx(DefaultClassnames.today, "today"),
          }}
          onSelect={onChange}
          onMonthChange={(month) => {
            setCurrentMonth(moment(month));
            onMonthChange?.(month);
          }}
        />
      </Popover>
    </>
  );
};

export default CommonDayPicker;

const checkIfYearIsSelected = ({ year, minDate, maxDate, value }) => {
  if (!value || !value.from || !value.to) return false;

  let yearStart = moment(`${year}-01-01`);
  let yearEnd = moment(`${year}-12-31`);

  // Adjust year boundaries based on minDate/maxDate constraints
  if (minDate && moment(minDate).year() === parseInt(year)) {
    yearStart = moment(minDate);
  }

  if (maxDate && moment(maxDate).year() === parseInt(year)) {
    yearEnd = moment(maxDate);
  }

  return (
    moment(value.from).isSame(yearStart, "day") &&
    moment(value.to).isSame(yearEnd, "day")
  );
};

const checkIfMonthIsOutOfRange = ({ monthDate, minDate, maxDate }) => {
  const monthStart = moment(monthDate).startOf("month");
  const monthEnd = moment(monthDate).endOf("month");

  if (minDate && monthEnd.isBefore(moment(minDate), "day")) {
    return true;
  }

  if (maxDate && monthStart.isAfter(moment(maxDate), "day")) {
    return true;
  }
  return false;
};
